import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Dialog,
  OpenDialogActionPayload,
} from '../../../models/modals/dialog.models';
import {
  EventModal,
  GameModal,
  Modal,
  OpenEventModalActionPayload,
  OpenGameModalActionPayload,
  OpenModalActionPayload,
  OpenPracticeModalActionPayload,
  OpenTutorialModalActionPayload,
  PracticeModal,
  TutorialModal,
} from '../../../models/modals/modal.models';

export const MODALS_FEATURE_KEY = 'modals';

export interface ModalsState {
  dialog: Dialog;
  modal: Modal;
  gameModal: GameModal;
  practiceModal: PracticeModal;
  eventModal: EventModal;
  tutorialModal: TutorialModal;
}

const initialModalsState: ModalsState = {
  dialog: {
    open: false,
    loading: false,
    title: '',
    text: '',
    onOk: () => null,
  },
  modal: {
    open: false,
    loading: false,
    title: '',
    formId: null,
    component: null,
    onOk: () => null,
    okText: null,
    hideCancelButton: false,
    hideOkButton: false,
    width: null,
    zIndex: 1000,
  },
  gameModal: {
    open: false,
    loading: false,
    id: 0,
  },
  practiceModal: {
    open: false,
    loading: false,
    id: 0,
  },
  eventModal: {
    open: false,
    loading: false,
    id: 0,
  },
  tutorialModal: {
    open: false,
    loading: false,
    id: 0,
  },
};

export const _modalsSlice = createSlice({
  name: MODALS_FEATURE_KEY,
  initialState: initialModalsState,
  reducers: {
    /****************************************************************************************************
     *
     *                                        DIALOG REDUCER
     *
     * **************************************************************************************************
     */
    openDialog: (state, action: PayloadAction<OpenDialogActionPayload>) => {
      state.dialog.open = true;
      state.dialog.title = action.payload.title;
      state.dialog.text = action.payload.text;
      state.dialog.onOk = (closeDialog: () => void) =>
        action.payload.onOk(closeDialog);
    },
    closeDialog: (state) => {
      state.dialog.open = false;
      state.dialog.loading = false;
      state.dialog.onOk = () => null;
    },
    setDialogLoading: (state, action) => {
      state.dialog.loading = action.payload;
    },
    /****************************************************************************************************
     *
     *                                        MODAL REDUCER
     *
     * **************************************************************************************************
     */
    openModal: (state, action: PayloadAction<OpenModalActionPayload>) => {
      state.modal.open = true;
      state.modal.title = action.payload.title;
      state.modal.formId = action.payload.formId;
      state.modal.component = action.payload.component;
      state.modal.okText = action.payload.okText;
      state.modal.hideCancelButton = action.payload.hideCancelButton;
      state.modal.hideOkButton = action.payload.hideOkButton;
      state.modal.width = action.payload.width;
      state.modal.lightBackground = action.payload.lightBackground;
      state.modal.closable = action.payload.closable;
      state.modal.zIndex = action.payload.zIndex;
      state.modal.onOk = (closeModal: () => void) => {
        if (action.payload.onOk) {
          action.payload.onOk(closeModal);
        }
      };
    },
    closeModal: (state) => {
      state.modal.open = false;
      state.modal.loading = false;
    },
    setModalLoading: (state, action) => {
      state.modal.loading = action.payload;
    },
    /****************************************************************************************************
     *
     *                                        GAME MODAL REDUCER
     *
     * **************************************************************************************************
     */
    openGameModal: (
      state,
      action: PayloadAction<OpenGameModalActionPayload>
    ) => {
      state.gameModal.id = action.payload.id;
      state.gameModal.open = true;
    },
    closeGameModal: (state) => {
      state.gameModal.open = false;
    },
    /****************************************************************************************************
     *
     *                                        PRACTICE MODAL REDUCER
     *
     * **************************************************************************************************
     */
    openPracticeModal: (
      state,
      action: PayloadAction<OpenPracticeModalActionPayload>
    ) => {
      state.practiceModal.id = action.payload.id;
      state.practiceModal.open = true;
    },
    closePracticeModal: (state) => {
      state.practiceModal.open = false;
    },
    /****************************************************************************************************
     *
     *                                        EVENT MODAL REDUCER
     *
     * **************************************************************************************************
     */
    openEventModal: (
      state,
      action: PayloadAction<OpenEventModalActionPayload>
    ) => {
      state.eventModal.id = action.payload.id;
      state.eventModal.open = true;
    },
    closeEventModal: (state) => {
      state.eventModal.open = false;
    },
    /****************************************************************************************************
     *
     *                                        TUTORIAL MODAL REDUCER
     *
     * **************************************************************************************************
     */
    openTutorialModal: (
      state,
      action: PayloadAction<OpenTutorialModalActionPayload>
    ) => {
      state.tutorialModal.id = action.payload.id;
      state.tutorialModal.open = true;
    },
    closeTutorialModal: (state) => {
      state.tutorialModal.open = false;
    },
  },
});

/*
 * Export reducer for store configuration.
 */
export const modalsReducer = _modalsSlice.reducer;
export const modalsActions = _modalsSlice.actions;

const getModalsState = (rootState: any): ModalsState =>
  rootState[MODALS_FEATURE_KEY];

export const modalsState = createSelector(getModalsState, (state) => state);
